import { View, Text, ScrollView, Platform, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { useStyles } from '../hooks/useStyles';
import { useResponsive, getResponsiveValue } from '../shared/utils/responsive';
import { addColorOpacity } from '../shared/utils/styles';
import { Ionicons } from '@expo/vector-icons';
import { useEffect, useRef } from 'react';
import Animated, { 
  useSharedValue, 
  useAnimatedStyle, 
  withSpring, 
  withTiming,
  withSequence,
  withDelay,
  FadeInDown,
  FadeInUp,
} from 'react-native-reanimated';

export default function LandingPage() {
  const router = useRouter();
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  const { isMobile, isTablet, isDesktop, width } = responsive;
  
  // Animation values
  const logoScale = useSharedValue(1);
  const buttonScale = useSharedValue(1);
  
  // Feature card animations (4 features now)
  const featureCards = useRef(
    Array.from({ length: 4 }, () => ({
      scale: useSharedValue(1),
      opacity: useSharedValue(0),
    }))
  ).current;

  // How it works step animations
  const stepCards = useRef(
    Array.from({ length: 4 }, () => ({
      scale: useSharedValue(1),
      opacity: useSharedValue(0),
    }))
  ).current;

  // Create all animated styles at component level
  const featureCardStyles = featureCards.map((card, index) => 
    useAnimatedStyle(() => ({
      opacity: card.opacity.value,
      transform: [{ scale: card.scale.value }],
    }))
  );

  const stepCardStyles = stepCards.map((card, index) => 
    useAnimatedStyle(() => ({
      opacity: card.opacity.value,
      transform: [{ scale: card.scale.value }],
    }))
  );

  useEffect(() => {
    logoScale.value = withSequence(
      withTiming(0, { duration: 0 }),
      withSpring(1, { damping: 8, stiffness: 100 })
    );

    featureCards.forEach((card, index) => {
      card.opacity.value = withDelay(
        index * 100,
        withTiming(1, { duration: 400 })
      );
      card.scale.value = withDelay(
        index * 100,
        withSpring(1, { damping: 10, stiffness: 100 })
      );
    });

    stepCards.forEach((card, index) => {
      card.opacity.value = withDelay(
        index * 120,
        withTiming(1, { duration: 400 })
      );
      card.scale.value = withDelay(
        index * 120,
        withSpring(1, { damping: 10, stiffness: 100 })
      );
    });
  }, []);

  const logoAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: logoScale.value }],
  }));

  const handleCardPressIn = (index: number, cards: typeof featureCards) => {
    cards[index].scale.value = withSpring(0.97, { damping: 15 });
  };

  const handleCardPressOut = (index: number, cards: typeof featureCards) => {
    cards[index].scale.value = withSpring(1, { damping: 15 });
  };

  const buttonAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: buttonScale.value }],
  }));

  const handleButtonPressIn = () => {
    buttonScale.value = withSpring(0.96, { damping: 15, stiffness: 300 });
  };

  const handleButtonPressOut = () => {
    buttonScale.value = withSpring(1, { damping: 15, stiffness: 300 });
  };

  // Responsive values
  const containerMaxWidth = getResponsiveValue(1200, 1200, 1200, 1400);
  const heroPadding = getResponsiveValue(
    utils.spacing.lg,
    utils.spacing.xl,
    utils.spacing['2xl'],
    utils.spacing['3xl']
  );
  const sectionPadding = getResponsiveValue(
    utils.spacing.lg,
    utils.spacing.xl,
    utils.spacing['2xl'],
    utils.spacing['3xl']
  );
  const headingSize = getResponsiveValue(28, 34, 42, 48);
  const subtitleSize = getResponsiveValue(16, 18, 20, 22);
  const bodySize = getResponsiveValue(15, 16, 17, 18);
  const buttonSize = getResponsiveValue(16, 17, 18, 18);
  const sectionHeadingSize = getResponsiveValue(24, 28, 32, 36);

  // How it Works steps - simple journey
  const howItWorks = [
    { 
      step: '1', 
      icon: 'folder-open' as const, 
      title: 'Create a Portfolio', 
      desc: 'Group your properties together and invite your partners to join.',
      color: tokens.color.primary,
    },
    { 
      step: '2', 
      icon: 'search' as const, 
      title: 'Add Properties', 
      desc: 'Add properties you\'re looking at, buying, or already own.',
      color: tokens.color.secondary,
    },
    { 
      step: '3', 
      icon: 'calculator' as const, 
      title: 'Run the Numbers', 
      desc: 'Use mortgage calculators and scenarios to see if a deal makes sense.',
      color: tokens.color.success,
    },
    { 
      step: '4', 
      icon: 'chatbubbles' as const, 
      title: 'Decide Together', 
      desc: 'Discuss with partners, track who owns what, and make decisions.',
      color: tokens.color.primary,
    },
  ];

  // Features - what you can do
  const features = [
    { 
      icon: 'people' as const, 
      title: 'Partners & Ownership', 
      desc: 'Invite partners, set ownership percentages for each property, and always know who owns what share.',
      color: tokens.color.primary,
    },
    { 
      icon: 'calculator' as const, 
      title: 'Mortgage Calculator', 
      desc: 'Calculate payments, see amortization schedules, and know exactly what each partner owes.',
      color: tokens.color.secondary,
    },
    { 
      icon: 'chatbubbles' as const, 
      title: 'Discussions & Notes', 
      desc: 'Have conversations about properties, leave notes, and keep everyone in the loop.',
      color: tokens.color.success,
    },
    { 
      icon: 'person' as const, 
      title: 'Contact Book', 
      desc: 'Keep track of lenders, agents, contractors, and other professionals you work with.',
      color: tokens.color.primary,
    },
  ];

  return (
    <View style={[
      styles.container,
      {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        ...Platform.select({
          web: {
            minHeight: '100vh',
            overflowX: 'hidden',
          } as any,
        }),
      },
    ]}>
      <ScrollView 
        contentContainerStyle={{
          flexGrow: 1,
          width: '100%',
          maxWidth: '100%',
          ...Platform.select({
            web: {
              overflowX: 'hidden',
            } as any,
          }),
        }}
        showsVerticalScrollIndicator={false}
        style={{
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* Hero Section */}
        <View style={{
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: heroPadding,
          paddingVertical: getResponsiveValue(
            utils.spacing['2xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl']
          ),
          alignItems: 'center',
          backgroundColor: tokens.color.background,
          ...Platform.select({
            web: {
              minHeight: isMobile ? 'auto' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxSizing: 'border-box',
            } as any,
          }),
        }}>
          <View style={{
            width: '100%',
            maxWidth: containerMaxWidth,
            alignItems: 'center',
          }}>
            {/* Logo */}
            <Animated.View 
              style={[
                {
                  width: getResponsiveValue(72, 80, 90, 100),
                  height: getResponsiveValue(72, 80, 90, 100),
                  borderRadius: utils.radius.xl,
                  backgroundColor: tokens.color.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: utils.spacing.lg,
                  ...Platform.select({
                    web: {
                      boxShadow: `0 8px 32px ${addColorOpacity(tokens.color.primary, 0.25)}`,
                    } as any,
                    default: {
                      shadowColor: tokens.color.primary,
                      shadowOffset: { width: 0, height: 8 },
                      shadowOpacity: 0.25,
                      shadowRadius: 32,
                      elevation: 8,
                    },
                  }),
                },
                logoAnimatedStyle,
              ]}
            >
              <Ionicons 
                name="leaf" 
                size={getResponsiveValue(36, 40, 44, 48)} 
                color={tokens.color.onPrimary} 
              />
            </Animated.View>

            {/* App Name */}
            <Animated.Text 
              entering={FadeInUp.delay(150).springify()}
              style={{
                fontSize: getResponsiveValue(14, 15, 16, 16),
                fontWeight: utils.typography.weights.semibold,
                color: tokens.color.primary,
                textAlign: 'center',
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: utils.spacing.sm,
                fontFamily: utils.typography.body.fontFamily,
              }}
            >
              Farmer John
            </Animated.Text>

            {/* Main Headline */}
            <Animated.Text 
              entering={FadeInUp.delay(200).springify()}
              style={{
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                color: tokens.color.onBackground,
                textAlign: 'center',
                letterSpacing: -0.5,
                lineHeight: headingSize * 1.15,
                marginBottom: utils.spacing.md,
                fontFamily: utils.typography.heading.fontFamily,
                width: '100%',
                maxWidth: 800,
                paddingHorizontal: utils.spacing.sm,
              }}
            >
              Buy & Manage Land{'\n'}With Your Partners
            </Animated.Text>
            
            {/* Value Proposition */}
            <Animated.Text 
              entering={FadeInUp.delay(300).springify()}
              style={{
                fontSize: subtitleSize,
                color: addColorOpacity(tokens.color.onBackground, 0.8),
                textAlign: 'center',
                lineHeight: subtitleSize * 1.5,
                marginBottom: utils.spacing.lg,
                fontFamily: utils.typography.body.fontFamily,
                width: '100%',
                maxWidth: 600,
                paddingHorizontal: utils.spacing.md,
              }}
            >
              One place to organize properties, run the numbers, track ownership, and make decisions together.
            </Animated.Text>

            {/* Problem Statement */}
            <Animated.View 
              entering={FadeInUp.delay(400).springify()}
              style={{
                backgroundColor: addColorOpacity(tokens.color.primary, 0.08),
                borderRadius: utils.radius.lg,
                paddingHorizontal: utils.spacing.lg,
                paddingVertical: utils.spacing.md,
                marginBottom: utils.spacing.xl,
                maxWidth: 550,
                width: '100%',
                borderLeftWidth: 4,
                borderLeftColor: tokens.color.primary,
              }}
            >
              <Text style={{
                fontSize: bodySize,
                color: tokens.color.onBackground,
                textAlign: 'left',
                lineHeight: bodySize * 1.6,
                fontFamily: utils.typography.body.fontFamily,
                fontStyle: 'italic',
              }}>
                "We were using spreadsheets, texts, and napkin math to figure out who owes what. 
                Now everything is in one place and everyone can see the same numbers."
              </Text>
            </Animated.View>

            {/* CTA Section */}
            <View style={{
              width: '100%',
              maxWidth: 400,
              paddingHorizontal: utils.spacing.sm,
            }}>
              <Animated.View 
                entering={FadeInUp.delay(500).springify()}
                style={[buttonAnimatedStyle, { width: '100%' }]}
              >
                <Pressable
                  onPressIn={handleButtonPressIn}
                  onPressOut={handleButtonPressOut}
                  onPress={() => router.push('/(auth)/login')}
                  style={({ pressed }) => [
                    {
                      backgroundColor: tokens.color.primary,
                      minHeight: getResponsiveValue(52, 54, 56, 56),
                      paddingHorizontal: utils.spacing.xl,
                      paddingVertical: utils.spacing.md,
                      borderRadius: utils.radius.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: utils.spacing.md,
                      width: '100%',
                      ...Platform.select({
                        web: {
                          boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        } as any,
                        default: {
                          shadowColor: tokens.color.primary,
                          shadowOffset: { width: 0, height: 6 },
                          shadowOpacity: 0.35,
                          shadowRadius: 20,
                          elevation: 8,
                          opacity: pressed ? 0.9 : 1,
                        },
                      }),
                    },
                  ]}
                >
                  <Text style={{
                    fontSize: buttonSize,
                    fontWeight: utils.typography.weights.semibold,
                    letterSpacing: 0.3,
                    color: tokens.color.onPrimary,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    Get Started
                  </Text>
                </Pressable>
              </Animated.View>
              
              <Text style={{
                fontSize: getResponsiveValue(13, 14, 15, 15),
                color: addColorOpacity(tokens.color.onBackground, 0.6),
                textAlign: 'center',
                marginTop: utils.spacing.xs,
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: getResponsiveValue(18, 20, 22, 22),
              }}>
                Sign in or request access if you're new
              </Text>
            </View>
          </View>
        </View>

        {/* How It Works Section */}
        <View style={{
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: sectionPadding,
          paddingVertical: getResponsiveValue(
            utils.spacing['2xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl']
          ),
          backgroundColor: tokens.color.surface,
        }}>
          <View style={{
            width: '100%',
            maxWidth: containerMaxWidth,
            alignSelf: 'center',
          }}>
            <Text style={{
              fontSize: sectionHeadingSize,
              fontWeight: utils.typography.weights.bold,
              textAlign: 'center',
              marginBottom: utils.spacing.sm,
              color: tokens.color.onBackground,
              fontFamily: utils.typography.heading.fontFamily,
            }}>
              How It Works
            </Text>
            <Text style={{
              fontSize: bodySize,
              color: addColorOpacity(tokens.color.onBackground, 0.7),
              textAlign: 'center',
              marginBottom: utils.spacing['2xl'],
              fontFamily: utils.typography.body.fontFamily,
              maxWidth: 500,
              alignSelf: 'center',
            }}>
              From finding properties to owning them together
            </Text>

            <View style={{
              width: '100%',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: utils.spacing.md,
              ...Platform.select({
                web: {
                  display: 'grid',
                  gridTemplateColumns: isMobile 
                    ? '1fr' 
                    : isTablet 
                    ? 'repeat(2, 1fr)' 
                    : 'repeat(4, 1fr)',
                  gap: `${utils.spacing.lg}px`,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                } as any,
              }),
            }}>
              {howItWorks.map((item, index) => (
                <Animated.View
                  key={index}
                  entering={FadeInDown.delay(index * 100).springify()}
                  style={[
                    {
                      width: isMobile ? '100%' : '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      flex: 1,
                      ...Platform.select({
                        web: {
                          boxSizing: 'border-box',
                        } as any,
                      }),
                    },
                    stepCardStyles[index],
                  ]}
                >
                  <View style={{
                    alignItems: 'center',
                    padding: utils.spacing.lg,
                    backgroundColor: tokens.color.background,
                    borderRadius: utils.radius.xl,
                    borderWidth: 1,
                    borderColor: tokens.color.border,
                    minHeight: getResponsiveValue(180, 190, 200, 200),
                    ...Platform.select({
                      web: {
                        boxShadow: `0 2px 8px ${addColorOpacity('#000', 0.06)}`,
                      } as any,
                      default: {
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.06,
                        shadowRadius: 8,
                        elevation: 2,
                      },
                    }),
                  }}>
                    {/* Step Number */}
                    <View style={{
                      width: getResponsiveValue(40, 44, 48, 48),
                      height: getResponsiveValue(40, 44, 48, 48),
                      borderRadius: 999,
                      backgroundColor: item.color,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: utils.spacing.md,
                    }}>
                      <Text style={{
                        fontSize: getResponsiveValue(18, 20, 22, 22),
                        fontWeight: utils.typography.weights.bold,
                        color: tokens.color.onPrimary,
                        fontFamily: utils.typography.heading.fontFamily,
                      }}>
                        {item.step}
                      </Text>
                    </View>
                    
                    {/* Icon */}
                    <View style={{
                      width: getResponsiveValue(48, 52, 56, 56),
                      height: getResponsiveValue(48, 52, 56, 56),
                      borderRadius: utils.radius.lg,
                      backgroundColor: addColorOpacity(item.color, 0.1),
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: utils.spacing.md,
                    }}>
                      <Ionicons 
                        name={item.icon} 
                        size={getResponsiveValue(24, 26, 28, 28)} 
                        color={item.color} 
                      />
                    </View>

                    <Text style={{
                      fontSize: getResponsiveValue(16, 17, 18, 18),
                      fontWeight: utils.typography.weights.bold,
                      color: tokens.color.onBackground,
                      marginBottom: utils.spacing.sm,
                      fontFamily: utils.typography.heading.fontFamily,
                      textAlign: 'center',
                    }}>
                      {item.title}
                    </Text>
                    <Text style={{
                      fontSize: getResponsiveValue(14, 14, 15, 15),
                      color: addColorOpacity(tokens.color.onBackground, 0.7),
                      lineHeight: getResponsiveValue(20, 21, 22, 22),
                      fontFamily: utils.typography.body.fontFamily,
                      textAlign: 'center',
                    }}>
                      {item.desc}
                    </Text>
                  </View>
                </Animated.View>
              ))}
            </View>
          </View>
        </View>

        {/* Features Section */}
        <View style={{
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: sectionPadding,
          paddingVertical: getResponsiveValue(
            utils.spacing['2xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl']
          ),
          backgroundColor: tokens.color.background,
        }}>
          <View style={{
            width: '100%',
            maxWidth: containerMaxWidth,
            alignSelf: 'center',
          }}>
            <Text style={{
              fontSize: sectionHeadingSize,
              fontWeight: utils.typography.weights.bold,
              textAlign: 'center',
              marginBottom: utils.spacing.sm,
              color: tokens.color.onBackground,
              fontFamily: utils.typography.heading.fontFamily,
            }}>
              What You Can Do
            </Text>
            <Text style={{
              fontSize: bodySize,
              color: addColorOpacity(tokens.color.onBackground, 0.7),
              textAlign: 'center',
              marginBottom: utils.spacing['2xl'],
              fontFamily: utils.typography.body.fontFamily,
              maxWidth: 500,
              alignSelf: 'center',
            }}>
              Tools built for people investing in land together
            </Text>

            <View style={{
              width: '100%',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: utils.spacing.lg,
              ...Platform.select({
                web: {
                  display: 'grid',
                  gridTemplateColumns: isMobile 
                    ? '1fr' 
                    : isTablet 
                    ? 'repeat(2, 1fr)' 
                    : 'repeat(2, 1fr)',
                  gap: `${utils.spacing.lg}px`,
                  width: '100%',
                  maxWidth: 900,
                  alignSelf: 'center',
                  boxSizing: 'border-box',
                } as any,
              }),
            }}>
              {features.map((feature, index) => (
                <Animated.View
                  key={index}
                  entering={FadeInDown.delay(index * 80).springify()}
                  style={[
                    {
                      width: isMobile ? '100%' : '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      flex: 1,
                      ...Platform.select({
                        web: {
                          minWidth: isMobile ? '100%' : 280,
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                        } as any,
                      }),
                    },
                    featureCardStyles[index],
                  ]}
                >
                  <Pressable
                    onPressIn={() => handleCardPressIn(index, featureCards)}
                    onPressOut={() => handleCardPressOut(index, featureCards)}
                    style={({ pressed }) => [
                      {
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        padding: utils.spacing.lg,
                        backgroundColor: tokens.color.surface,
                        borderRadius: utils.radius.xl,
                        borderWidth: 1,
                        borderColor: tokens.color.border,
                        minHeight: getResponsiveValue(120, 130, 140, 140),
                        gap: utils.spacing.md,
                        ...Platform.select({
                          web: {
                            boxShadow: `0 2px 8px ${addColorOpacity('#000', 0.06)}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          } as any,
                          default: {
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.06,
                            shadowRadius: 8,
                            elevation: 2,
                            opacity: pressed ? 0.9 : 1,
                          },
                        }),
                      },
                    ]}
                  >
                    <View style={{
                      width: getResponsiveValue(44, 48, 52, 52),
                      height: getResponsiveValue(44, 48, 52, 52),
                      borderRadius: utils.radius.lg,
                      backgroundColor: addColorOpacity(feature.color, 0.12),
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Ionicons 
                        name={feature.icon} 
                        size={getResponsiveValue(22, 24, 26, 26)} 
                        color={feature.color} 
                      />
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{
                        fontSize: getResponsiveValue(16, 17, 18, 18),
                        fontWeight: utils.typography.weights.bold,
                        color: tokens.color.onBackground,
                        marginBottom: utils.spacing.xs,
                        fontFamily: utils.typography.heading.fontFamily,
                      }}>
                        {feature.title}
                      </Text>
                      <Text style={{
                        fontSize: getResponsiveValue(14, 14, 15, 15),
                        color: addColorOpacity(tokens.color.onBackground, 0.7),
                        lineHeight: getResponsiveValue(20, 21, 22, 22),
                        fontFamily: utils.typography.body.fontFamily,
                      }}>
                        {feature.desc}
                      </Text>
                    </View>
                  </Pressable>
                </Animated.View>
              ))}
            </View>
          </View>
        </View>

        {/* Coming Soon Section */}
        <View style={{
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: sectionPadding,
          paddingVertical: getResponsiveValue(
            utils.spacing.xl,
            utils.spacing['2xl'],
            utils.spacing['2xl'],
            utils.spacing['2xl']
          ),
          backgroundColor: tokens.color.surface,
        }}>
          <View style={{
            width: '100%',
            maxWidth: 700,
            alignSelf: 'center',
            alignItems: 'center',
          }}>
            <Text style={{
              fontSize: getResponsiveValue(18, 20, 22, 22),
              fontWeight: utils.typography.weights.semibold,
              textAlign: 'center',
              marginBottom: utils.spacing.md,
              color: tokens.color.onBackground,
              fontFamily: utils.typography.heading.fontFamily,
            }}>
              Coming Soon
            </Text>
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: utils.spacing.sm,
            }}>
              {['What-If Scenarios', 'Map View', 'Due Diligence Checklists', 'Task Management', 'Voting'].map((item, index) => (
                <View key={index} style={{
                  backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
                  paddingHorizontal: utils.spacing.md,
                  paddingVertical: utils.spacing.sm,
                  borderRadius: utils.radius.full,
                }}>
                  <Text style={{
                    fontSize: getResponsiveValue(13, 14, 14, 14),
                    color: tokens.color.primary,
                    fontWeight: utils.typography.weights.medium,
                    fontFamily: utils.typography.body.fontFamily,
                  }}>
                    {item}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* Bottom CTA */}
        <View style={{
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: sectionPadding,
          paddingVertical: getResponsiveValue(
            utils.spacing['2xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl']
          ),
          backgroundColor: tokens.color.background,
          alignItems: 'center',
        }}>
          <View style={{
            width: '100%',
            maxWidth: 500,
            alignItems: 'center',
          }}>
            <Text style={{
              fontSize: getResponsiveValue(22, 26, 30, 32),
              fontWeight: utils.typography.weights.bold,
              textAlign: 'center',
              marginBottom: utils.spacing.md,
              color: tokens.color.onBackground,
              fontFamily: utils.typography.heading.fontFamily,
            }}>
              Ready to Get Started?
            </Text>
            <Text style={{
              fontSize: bodySize,
              color: addColorOpacity(tokens.color.onBackground, 0.7),
              textAlign: 'center',
              marginBottom: utils.spacing.xl,
              fontFamily: utils.typography.body.fontFamily,
              lineHeight: bodySize * 1.5,
            }}>
              Join your partners and start managing your land investments in one place.
            </Text>

            <Pressable
              onPress={() => router.push('/(auth)/login')}
              style={({ pressed }) => [
                {
                  backgroundColor: tokens.color.primary,
                  minHeight: getResponsiveValue(48, 50, 52, 52),
                  paddingHorizontal: utils.spacing['2xl'],
                  paddingVertical: utils.spacing.md,
                  borderRadius: utils.radius.md,
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...Platform.select({
                    web: {
                      boxShadow: `0 4px 16px ${addColorOpacity(tokens.color.primary, 0.3)}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    } as any,
                    default: {
                      shadowColor: tokens.color.primary,
                      shadowOffset: { width: 0, height: 4 },
                      shadowOpacity: 0.3,
                      shadowRadius: 16,
                      elevation: 6,
                      opacity: pressed ? 0.9 : 1,
                    },
                  }),
                },
              ]}
            >
              <Text style={{
                fontSize: buttonSize,
                fontWeight: utils.typography.weights.semibold,
                color: tokens.color.onPrimary,
                fontFamily: utils.typography.body.fontFamily,
              }}>
                Sign In
              </Text>
            </Pressable>
          </View>
        </View>

        {/* Footer */}
        <View style={{
          width: '100%',
          paddingVertical: utils.spacing.lg,
          paddingHorizontal: sectionPadding,
          backgroundColor: tokens.color.surface,
          borderTopWidth: 1,
          borderTopColor: tokens.color.border,
        }}>
          <Text style={{
            fontSize: getResponsiveValue(12, 13, 13, 13),
            color: addColorOpacity(tokens.color.onBackground, 0.5),
            textAlign: 'center',
            fontFamily: utils.typography.body.fontFamily,
          }}>
            Farmer John • Built for partners investing in land together
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}
